import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import api from '../../services/api';
import styles from './PhotoModal.module.css';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';

const PhotoViewerModal = ({ photos, isOpen, onClose, initialIndex = 0, autoOpenLightbox = false }) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);

  useEffect(() => {
    if (!isOpen) {
      setIsLightboxOpen(false);
      return;
    }
    setPhotoIndex(initialIndex || 0);
    setIsLightboxOpen(Boolean(autoOpenLightbox));
  }, [autoOpenLightbox, initialIndex, isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  const getAbsolutePhotoUrl = (photoUrl) => {
    if (!photoUrl) return '';
    if (photoUrl.startsWith('http://') || photoUrl.startsWith('https://')) {
      return photoUrl;
    }
    const baseUrlWithoutApi = api.defaults.baseURL.replace(/\/api\/$/, '');
    return `${baseUrlWithoutApi}${photoUrl}`;
  };

  const handleMediaClick = (index) => {
    setPhotoIndex(index);
    setIsLightboxOpen(true);
  };

  const lightboxSlides = photos.map(photo => ({
    src: getAbsolutePhotoUrl(photo.photo_url),
    alt: photo.description || 'Файл оборудования',
    type: photo.file_type, // image или video
  }));

  return createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby="photo-viewer-title">
        <div className={styles.modalHeader}>
          <h2 id="photo-viewer-title">Просмотр файлов</h2>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Закрыть окно">
            <X size={20} />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={styles.photoList}>
            <div className={styles.photoListHeader}>
              <h3>Файлы</h3>
              {photos.length > 0 && <span>{photos.length}</span>}
            </div>
            {photos.length > 0 ? (
              <>
                <div className={styles.photoGrid}>
                  {photos.map((photo, index) => (
                    <div key={photo.id} className={styles.photoItem}>
                      {photo.file_type === 'image' ? (
                        <img
                          src={getAbsolutePhotoUrl(photo.photo_url)}
                          alt={photo.description || 'Фото оборудования'}
                          className={styles.photoPreview}
                          onClick={() => handleMediaClick(index)}
                          style={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <video
                          src={getAbsolutePhotoUrl(photo.photo_url)}
                          className={styles.photoPreview}
                          controls
                          muted
                          onClick={() => handleMediaClick(index)}
                          style={{ cursor: 'pointer' }}
                        />
                      )}
                      <div className={styles.photoDetails}>
                        <p>{photo.description || 'Без описания'}</p>
                        <p>Загружено: {new Date(photo.uploaded_at).toLocaleString()}</p>
                        <p>Пользователь: {photo.uploaded_by_name || photo.uploaded_by || 'Инженер'}</p>
                      </div>
                    </div>
                  ))}
                </div>
                {isLightboxOpen && (
                  <Lightbox
                    open={isLightboxOpen}
                    close={() => setIsLightboxOpen(false)}
                    slides={lightboxSlides.map(slide => ({
                      ...slide,
                      type: slide.type === 'video' ? 'video' : 'image',
                      sources: slide.type === 'video' ? [{ src: slide.src, type: 'video/mp4' }] : undefined,
                    }))}
                    index={photoIndex}
                    on={{
                      view: ({ index }) => setPhotoIndex(index),
                    }}
                    plugins={[Zoom]}
                    zoom={{
                      maxZoomPixelRatio: 3,
                      zoomInMultiplier: 2,
                      doubleTapDelay: 300,
                      doubleClickDelay: 300,
                      doubleClickMaxStops: 2,
                    }}
                    render={{
                      slide: ({ slide }) =>
                        slide.type === 'video' ? (
                          <video
                            src={slide.src}
                            controls
                            autoPlay
                            style={{ maxWidth: '100%', maxHeight: '100%' }}
                          />
                        ) : null,
                    }}
                  />
                )}
              </>
            ) : (
              <p className={styles.noPhotos}>Файлы отсутствуют</p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default PhotoViewerModal;
